import type { Case, CaseFile } from '@/modules/Cases/types'
import { computed, type Ref } from 'vue'

/**
 * Groups a Case's files into categories:
 * - attached: files attached to a message
 * - detached: files not attached to any message, and not in an error or uploading state
 * - detachedErrored: files not attached to a message, and in an error state
 * - detachedUploading: files not attached to a message yet, in an uploading state
 */
export const useCategorisedCaseFiles = (caseData: Ref<Case | undefined>) => {
  return computed<CaseFilesCategorized>(() => {
    if (!caseData.value) {
      return {
        attached: {},
        detached: [],
        detachedErrored: [],
        detachedUploading: [],
      }
    }

    const files = caseData.value.files || []
    const messages = caseData.value?.messages ?? []

    const filesByMessage: Record<string, CaseFile[]> = {}

    const attachedFileIds = new Set<string>()

    messages.forEach((message) => {
      if (message.type !== 'query') return
      const messageFiles: CaseFile[] = []

      message.fileIds.forEach((fileId) => {
        attachedFileIds.add(fileId)
        const file = files.find((f) => f.id === fileId)
        if (!file) return
        messageFiles.push(file)
      })
      filesByMessage[message.id] = messageFiles
    })

    return {
      attached: filesByMessage,
      detached: files.filter(
        (f) => !attachedFileIds.has(f.id) && f.status !== 'error' && f.status !== 'uploading',
      ),
      detachedErrored: files.filter((f) => !attachedFileIds.has(f.id) && f.status === 'error'),
      detachedUploading: files.filter(
        (f) => !attachedFileIds.has(f.id) && f.status === 'uploading',
      ),
    }
  })
}

type CaseFilesCategorized = {
  /**
   * Files attached to a message, mapped by message id
   */
  attached: Record<string, CaseFile[]>
  /**
   * Files not attached to a message, and not in an error or uploading state.
   * These could be done or are still processing. We can't currently delete them,
   * so we categorize them separately in order to not show them above message input.
   */
  detached: CaseFile[]
  /**
   * Files not attached to a message, and in an error state.
   * This got uploaded, but failed to process.
   * They will be shown as errored above the message input and can be deleted.
   */
  detachedErrored: CaseFile[]
  /**
   * Files not attached to a message, and in an uploading state.
   * This are registered but haven't been uploaded yet. In a multi-user environment,
   * this could be another user's upload, but with cases, it's likely the current user
   * has just aborted the upload somehow, for example by closing the tab.
   * They will be shown as errored above the message input and can be deleted.
   */
  detachedUploading: CaseFile[]
}
